/**
 * Storage module for regional API base URL and auth configuration.
 * Separated from AppWithAuth to avoid circular dependencies with httpClient.
 *
 * This module is used by:
 * - AppWithAuth.tsx: Sets the regional URL and auth config after Auth Router lookup
 * - httpClient.ts: Reads the regional URL for API requests
 */
import { BASE_URL } from '../externalConfig';

const REGIONAL_API_BASE_URL_KEY = 'regional_api_base_url';
const REGIONAL_AUTH_CONFIG_KEY = 'regional_auth_config';

/**
 * Gets the regional API base URL stored during Auth Router configuration.
 * Used by httpClient to route API calls to the correct region.
 */
export function getRegionalApiBaseUrl(): string | null {
  return sessionStorage.getItem(REGIONAL_API_BASE_URL_KEY);
}

/**
 * The API base to build STORED, absolute media URLs from (custom-activity video/gif/thumbnail).
 * These are persisted on the definition and fetched later by raw <img>/<video>/ExoPlayer that
 * bypass httpClient's per-request regional override — so the stored host MUST already be the user's
 * region. Uploads go to the regional API (httpClient), so the media must be served from the same
 * region's TPH/bucket; using the static (eu default) BASE_URL for a non-eu clinic pointed reads at
 * the wrong-region bucket where the object was never written (403). Mirrors httpClient's
 * getEffectiveBaseUrl; falls back to BASE_URL pre-login / single-region.
 */
export function getMediaBaseUrl(): string {
  return getRegionalApiBaseUrl() || BASE_URL;
}

/**
 * Sets the regional API base URL.
 * Called by AppWithAuth after Auth Router returns the user's region.
 */
export function setRegionalApiBaseUrl(url: string): void {
  sessionStorage.setItem(REGIONAL_API_BASE_URL_KEY, url);
}

/**
 * Stored auth region config for restoring Amplify configuration on page refresh.
 * When a user signs in via the Auth Router, Amplify is reconfigured to a different
 * region's Cognito pool. On refresh, this stored config allows re-applying that
 * configuration before Amplify tries to validate existing session tokens.
 */
export interface StoredAuthConfig {
  region: string;
  cognitoPoolId: string;
  clientId: string;
  apiBaseUrl: string;
  // The regional Cognito Identity Pool id. Persisted so a page refresh restores
  // the SAME identity pool the user was routed to at login. Without it, bootstrap
  // reverts the identity pool to the build-time (eu) default while the user pool
  // stays regional — a mismatched pair the eu identity pool won't federate, so
  // Auth.currentCredentials() (and thus RUM ingestion) fails post-refresh.
  // Optional: pre-existing stored blobs (and eu-default logins) may omit it.
  identityPoolId?: string;
}

export function getStoredAuthConfig(): StoredAuthConfig | null {
  const raw = sessionStorage.getItem(REGIONAL_AUTH_CONFIG_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as StoredAuthConfig;
  } catch {
    return null;
  }
}

export function setStoredAuthConfig(config: StoredAuthConfig): void {
  sessionStorage.setItem(REGIONAL_AUTH_CONFIG_KEY, JSON.stringify(config));
}

/**
 * Clears the regional API base URL and auth config on sign-out.
 */
export function clearRegionalApiBaseUrl(): void {
  sessionStorage.removeItem(REGIONAL_API_BASE_URL_KEY);
  sessionStorage.removeItem(REGIONAL_AUTH_CONFIG_KEY);
}
